import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { getUpvoteCount, getUserUpvoted, toggleUpvote, UpvoteTarget } from '@/api/upvotes';
import { HolidayUpvoteButton } from './HolidayUpvoteButton';

interface SmartUpvoteButtonProps {
  target: UpvoteTarget;
  id: string | number;
  holidayType?: string | null;
  initialCount?: number;
  className?: string;
  onRequireAuth?: () => void;
}

// Function to determine holiday theme from holiday_type string
function getHolidayTheme(holidayType: string | null): "christmas" | "halloween" | "fourth-of-july" {
  if (!holidayType) return 'christmas';
  
  const holiday = holidayType.toLowerCase().trim();
  
  // Christmas themes
  if (holiday.includes('christmas') || holiday.includes('xmas') || holiday === 'holiday') {
    return 'christmas';
  }
  
  // Halloween themes
  if (holiday.includes('halloween') || holiday.includes('spooky')) {
    return 'halloween';
  }
  
  // July 4th / Independence Day themes
  if (
    holiday.includes('july') || 
    holiday.includes('4th') || 
    holiday.includes('fourth') || 
    holiday.includes('independence') ||
    holiday.includes('patriotic')
  ) {
    return 'fourth-of-july';
  }
  
  // Default fallback to Christmas
  return 'christmas';
}

export const SmartUpvoteButton: React.FC<SmartUpvoteButtonProps> = ({
  target,
  id,
  holidayType,
  initialCount,
  className,
  onRequireAuth,
}) => {
  const { user, isAuthenticated } = useAuth();
  const [count, setCount] = useState<number>(initialCount ?? 0);
  const [upvoted, setUpvoted] = useState<boolean>(false);
  const [pending, setPending] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  // Determine the holiday theme
  const theme = getHolidayTheme(holidayType);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    
    (async () => {
      try {
        const c = initialCount ?? (await getUpvoteCount(target, id));
        const u = await getUserUpvoted(target, id, user?.id);
        if (!mounted) return;
        setCount(c);
        setUpvoted(u);
      } catch (error) {
        console.error('Error loading upvote data:', error);
        if (!mounted) return;
        // Keep defaults on error
        setCount(initialCount ?? 0);
        setUpvoted(false);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    })();
    
    return () => {
      mounted = false;
    };
  }, [target, id, user?.id, initialCount]);

  const handleUpvote = async (newUpvoted: boolean, newCount: number) => {
    if (!isAuthenticated || !user?.id) {
      onRequireAuth?.();
      return;
    }
    
    if (pending) return;
    
    setPending(true);
    
    // Store original state for rollback
    const originalUpvoted = upvoted;
    const originalCount = count;
    
    // Optimistic update
    setUpvoted(newUpvoted);
    setCount(newCount);
    
    try {
      const result = await toggleUpvote(target, id, user.id);
      
      // Use server response for authoritative state
      setUpvoted(result === 'added');
      
      // Get fresh count from server after toggle
      const freshCount = await getUpvoteCount(target, id);
      setCount(freshCount);
      
    } catch (e) {
      console.error('Error toggling upvote:', e);
      // Rollback on error
      setUpvoted(originalUpvoted);
      setCount(originalCount);
    } finally {
      setPending(false);
    }
  };

  return (
    <HolidayUpvoteButton
      initialUpvoted={upvoted}
      initialCount={count}
      theme={theme}
      onUpvote={handleUpvote}
      className={className}
      disabled={pending || loading}
    />
  );
};

export default SmartUpvoteButton;